import './styles/main.css'
import { WaitlistForm, InterviewForm } from './utils/forms.js'

class SuperIndividualApp {
  constructor() {
    this.init()
  }
  
  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.setupNavigation()
      this.setupForms()
      this.setupModals()
      this.setupSmoothScrolling()
      this.setupCharacterCounters()
      this.setupCSRFTokens()
      this.setupAnalytics()
    })
  }
  
  setupNavigation() {
    // Mobile menu toggle
    const menuToggle = document.getElementById('mobile-menu-toggle')
    const mobileMenu = document.getElementById('mobile-menu')
    
    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', () => {
        const isExpanded = menuToggle.getAttribute('aria-expanded') === 'true'
        
        menuToggle.setAttribute('aria-expanded', !isExpanded)
        mobileMenu.classList.toggle('hidden')
        
        // Update icon
        const icon = menuToggle.querySelector('svg')
        if (!isExpanded) {
          // Change to close icon
          icon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>'
        } else {
          // Change to hamburger icon
          icon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>'
        }
      })
      
      // Close menu when clicking outside
      document.addEventListener('click', (event) => {
        if (!menuToggle.contains(event.target) && !mobileMenu.contains(event.target)) {
          mobileMenu.classList.add('hidden')
          menuToggle.setAttribute('aria-expanded', 'false')
        }
      })
      
      // Close menu on escape key
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !mobileMenu.classList.contains('hidden')) {
          mobileMenu.classList.add('hidden')
          menuToggle.setAttribute('aria-expanded', 'false')
          menuToggle.focus()
        }
      })
    }
  }
  
  setupForms() {
    // Initialize waitlist form
    const waitlistForm = document.getElementById('waitlist-form')
    if (waitlistForm) {
      new WaitlistForm(waitlistForm)
    }
    
    // Initialize interview form
    const interviewForm = document.getElementById('interview-form')
    if (interviewForm) {
      new InterviewForm(interviewForm)
    }
  }
  
  setupModals() {
    // Interview Modal
    const interviewTriggers = document.querySelectorAll('#interview-modal-trigger, #interview-modal-trigger-2')
    const interviewModal = document.getElementById('interview-modal')
    const interviewClose = document.getElementById('interview-modal-close')
    const interviewCancel = document.getElementById('interview-cancel')
    
    if (interviewModal) {
      // Open modal
      interviewTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          interviewModal.classList.remove('hidden')
          interviewModal.classList.add('flex')
          
          // Focus first input for accessibility
          const firstInput = interviewModal.querySelector('input')
          if (firstInput) {
            setTimeout(() => firstInput.focus(), 100)
          }
          
          // Trap focus within modal
          this.trapFocus(interviewModal)
        })
      })
      
      // Close modal
      const closeModal = () => {
        interviewModal.classList.add('hidden')
        interviewModal.classList.remove('flex')
      }
      
      interviewClose?.addEventListener('click', closeModal)
      interviewCancel?.addEventListener('click', closeModal)
      
      // Close on escape key
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !interviewModal.classList.contains('hidden')) {
          closeModal()
        }
      })
      
      // Close on backdrop click
      interviewModal.addEventListener('click', (event) => {
        if (event.target === interviewModal) {
          closeModal()
        }
      })
    }
    
    // Video Modal
    const videoTrigger = document.getElementById('video-trigger')
    const videoModal = document.getElementById('video-modal')
    const videoClose = document.getElementById('video-modal-close')
    const videoIframe = document.getElementById('video-iframe')
    
    if (videoModal && videoIframe) {
      const openVideo = () => {
        // 使用 TED Talk 關於遠端工作的影片作為展示
        const videoUrl = 'https://www.youtube.com/embed/5XD2kNopsUs?autoplay=1'
        videoIframe.src = videoUrl
        
        videoModal.classList.remove('hidden')
        videoModal.classList.add('flex')
        
        // Track video play event
        if (typeof gtag !== 'undefined') {
          gtag('event', 'video_play', {
            event_category: 'engagement',
            event_label: 'intro_video'
          })
        }
      }
      
      const closeVideo = () => {
        videoIframe.src = ''
        videoModal.classList.add('hidden')
        videoModal.classList.remove('flex')
      }
      
      videoTrigger?.addEventListener('click', openVideo)
      videoClose?.addEventListener('click', closeVideo)
      
      // Close on escape key
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !videoModal.classList.contains('hidden')) {
          closeVideo()
        }
      })
      
      // Close on backdrop click
      videoModal.addEventListener('click', (event) => {
        if (event.target === videoModal) {
          closeVideo()
        }
      })
    }
  }
  
  setupSmoothScrolling() {
    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault()
        
        const target = document.querySelector(this.getAttribute('href'))
        if (target) {
          const headerHeight = document.querySelector('header')?.offsetHeight || 0
          const targetPosition = target.offsetTop - headerHeight - 20
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          })
        }
      })
    })
  }
  
  setupCharacterCounters() {
    // Background field counter
    const backgroundField = document.getElementById('waitlist-background')
    const backgroundCounter = document.getElementById('background-count')
    
    if (backgroundField && backgroundCounter) {
      backgroundField.addEventListener('input', () => {
        const count = backgroundField.value.length
        backgroundCounter.textContent = count
        
        // Change color when approaching limit
        if (count > 450) {
          backgroundCounter.classList.add('text-red-500')
        } else {
          backgroundCounter.classList.remove('text-red-500')
        }
      })
    }
    
    // Story field counter
    const storyField = document.getElementById('interview-story')
    const storyCounter = document.getElementById('story-count')
    
    if (storyField && storyCounter) {
      storyField.addEventListener('input', () => {
        const count = storyField.value.length
        storyCounter.textContent = count
        
        // Change color when approaching limit
        if (count > 450) {
          storyCounter.classList.add('text-red-500')
        } else {
          storyCounter.classList.remove('text-red-500')
        }
      })
    }
  }
  
  setupCSRFTokens() {
    // Generate and set CSRF tokens for forms
    const csrfToken = this.generateCSRFToken()
    
    const waitlistCSRF = document.getElementById('waitlist-csrf')
    const interviewCSRF = document.getElementById('interview-csrf')
    
    if (waitlistCSRF) {
      waitlistCSRF.value = csrfToken
    }
    
    if (interviewCSRF) {
      interviewCSRF.value = csrfToken
    }
  }
  
  generateCSRFToken() {
    // Simple CSRF token generation (in production, this should come from server)
    const array = new Uint8Array(32)
    crypto.getRandomValues(array)
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
  }
  
  setupAnalytics() {
    // Initialize scroll tracking for engagement
    let maxScrollPercentage = 0
    
    const trackScroll = () => {
      const scrollTop = window.pageYOffset
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollPercentage = Math.round((scrollTop / docHeight) * 100)
      
      if (scrollPercentage > maxScrollPercentage) {
        maxScrollPercentage = scrollPercentage
        
        // Track milestone percentages
        if ([25, 50, 75, 90].includes(scrollPercentage)) {
          if (typeof gtag !== 'undefined') {
            gtag('event', 'scroll', {
              event_category: 'engagement',
              event_label: `${scrollPercentage}_percent`,
              value: scrollPercentage
            })
          }
        }
      }
    }
    
    let ticking = false
    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(trackScroll)
        ticking = true
        setTimeout(() => { ticking = false }, 100)
      }
    }
    
    window.addEventListener('scroll', requestTick, { passive: true })
    
    // Track time on page
    const startTime = Date.now()
    
    window.addEventListener('beforeunload', () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000)
      
      if (typeof gtag !== 'undefined') {
        gtag('event', 'timing_complete', {
          name: 'page_view',
          value: timeSpent
        })
      }
    })
  }
  
  trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    
    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault()
            lastElement.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault()
            firstElement.focus()
          }
        }
      }
    }
    
    element.addEventListener('keydown', handleTabKey)
  }
}

// Initialize the application
new SuperIndividualApp()